/* addon-bundler.js
 * Blind Barrels — Subscription + Add-ons bundler
 * v3.3: annual / quarterly / one-time + per-plan discount + image swap + cart-first
 *       + "free sample requires subscription" config export for cart guard
 */


(function () {
  const SECTION_SELECTOR = '[data-subscription-toggle]';
  const TOGGLE_BTN_SELECTOR = '[data-plan-toggle]';
  const CTA_SELECTOR = '[data-plan-cta]';
  const SAMPLE_LINK_SELECTOR = '[data-sample-link]';
  const PLAN_CONTENT_SELECTOR = '[data-plan-content]';
  const PLAN_IMAGE_SELECTOR = '[data-plan-image]';
  const PLAN_IMAGE_MOBILE_SELECTOR = '[data-plan-image-mobile]';

  const q = (sel, root = document) => root.querySelector(sel);
  const qa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const toNum = (val) => {
    if (val == null || val === '') return null;
    const n = Number(val);
    return Number.isFinite(n) ? n : null;
  };

  async function addItems(items) {
    if (!items || !items.length) return;

    // Ask Shopify to also return the rendered cart-drawer + cart-icon-bubble
    // section HTML so we can re-render the drawer immediately. Without this,
    // the drawer shows stale contents until the user refreshes the page.
    const body = {
      items,
      sections: 'cart-drawer,cart-icon-bubble',
      sections_url: window.location.pathname
    };

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Add to cart failed: ${res.status}`);
    }

    return res.json();
  }

  // Push freshly-rendered section HTML from /cart/add.js into the cart drawer
  // and cart-icon-bubble. Safe no-op if the drawer isn't on the page.
  function refreshCartDrawerFromResponse(response) {
    if (!response || !response.sections) return false;
    const drawer = document.querySelector('cart-drawer');

    // This theme places the `is-empty` class on the <cart-drawer> custom
    // element (see snippets/cart-drawer.liquid line 19), not on .drawer__inner
    // where Dawn's stock renderContents looks. If we don't strip it manually,
    // the line items render into the DOM but stay hidden by CSS.
    if (drawer) drawer.classList.remove('is-empty');

    if (drawer && typeof drawer.renderContents === 'function') {
      try {
        drawer.renderContents(response);
        drawer.classList.remove('is-empty');
        // Dawn's renderContents schedules its own this.open() via setTimeout
        // (see assets/cart-drawer.js ~line 82-84). Signal 'native' so the
        // caller does NOT call drawer.open() a second time — a double open()
        // restarts the slide-in transition and is visible as a flicker.
        return 'native';
      } catch (e) {
        console.warn('[addon-bundler] drawer.renderContents failed:', e);
      }
    }
    // Manual fallback: replace innerHTML of the drawer + bubble sections.
    try {
      const parser = new DOMParser();
      const drawerHtml = response.sections['cart-drawer'];
      if (drawerHtml) {
        const drawerSection = document.getElementById('CartDrawer');
        if (drawerSection) {
          const fresh = parser.parseFromString(drawerHtml, 'text/html').querySelector('#CartDrawer');
          if (fresh) drawerSection.innerHTML = fresh.innerHTML;
        }
      }
      const bubbleHtml = response.sections['cart-icon-bubble'];
      if (bubbleHtml) {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          const fresh = parser.parseFromString(bubbleHtml, 'text/html').querySelector('#cart-icon-bubble');
          if (fresh) bubble.innerHTML = fresh.innerHTML;
        }
      }
      return 'manual';
    } catch (e) {
      console.warn('[addon-bundler] manual section refresh failed:', e);
      return false;
    }
  }

  function tryOpenCartDrawer() {
    // Primary: Dawn 15 CartDrawer custom element API
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    // Fallback: click header cart icon
    const triggerSelectors = [
      '#cart-icon-bubble',
      'button[aria-controls*="CartDrawer"]',
      '[data-drawer-id*="cart"]',
      '.js-toggle-cart',
      '.header__icon--cart, .site-header__cart',
      'button[name="open-cart"], a[href="#cart-drawer"]',
      'a[href="/cart"]'
    ];
    for (const sel of triggerSelectors) {
      const el = document.querySelector(sel);
      if (el) {
        el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        return true;
      }
    }
    return false;
  }

  // ---- Active discount cache ------------------------------------------------
  // Shopify serializes cart-session writes, so reapplying the same discount
  // costs ~500ms for no benefit. Cache the currently-active cart discount in
  // sessionStorage and skip the /cart/update.js call when it matches.
  const DISCOUNT_CACHE_KEY = 'bbActiveDiscountCode';
  function getCachedDiscountCode() {
    try { return sessionStorage.getItem(DISCOUNT_CACHE_KEY) || ''; } catch (e) { return ''; }
  }
  function setCachedDiscountCode(code) {
    try {
      if (code) sessionStorage.setItem(DISCOUNT_CACHE_KEY, code);
      else sessionStorage.removeItem(DISCOUNT_CACHE_KEY);
    } catch (e) { /* private mode */ }
  }

  // Apply a Shopify discount code to the cart without a page navigation.
  // Uses the documented `discount` field on POST /cart/update.js so the
  // discounted prices show up in the next /cart/add.js sections response.
  // De-dupes concurrent calls for the same code so a hover-prefetch in
  // flight is reused by the click handler instead of triggering a second
  // round-trip (Shopify serializes cart writes anyway).
  let pendingDiscountCode = '';
  let pendingDiscountPromise = null;
  async function applyDiscountCode(code) {
    if (!code) return true;
    if (getCachedDiscountCode() === code) return true; // already active
    if (pendingDiscountPromise && pendingDiscountCode === code) {
      return pendingDiscountPromise;
    }
    pendingDiscountCode = code;
    pendingDiscountPromise = (async () => {
      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ discount: code })
        });
        if (!res.ok) {
          console.warn('[addon-bundler] discount apply returned', res.status);
          return false;
        }
        setCachedDiscountCode(code);
        return true;
      } catch (e) {
        console.warn('[addon-bundler] discount apply failed:', e);
        return false;
      } finally {
        pendingDiscountCode = '';
        pendingDiscountPromise = null;
      }
    })();
    return pendingDiscountPromise;
  }

  // ---- Optimistic drawer open ----------------------------------------------
  // Open the drawer the instant the user clicks (before the network round-trip)
  // so the slide-in animation runs in parallel with /cart/update.js and
  // /cart/add.js. The drawer fills in real content when renderContents fires.
  const OPTIMISTIC_STYLE_ID = 'bb-addon-optimistic-style';
  function ensureOptimisticStyles() {
    if (document.getElementById(OPTIMISTIC_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = OPTIMISTIC_STYLE_ID;
    style.textContent = [
      'cart-drawer.bb-loading .drawer__inner { position: relative; }',
      'cart-drawer.bb-loading .drawer__inner > *:not(.bb-loading-overlay) { opacity: 0.18; pointer-events: none; transition: opacity 180ms cubic-bezier(0.4, 0, 0.2, 1); filter: blur(1px); }',
      '.bb-loading-overlay { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; background: radial-gradient(ellipse at center, rgba(26,26,26,0.78) 0%, rgba(26,26,26,0.62) 100%); color: #F5F5F5; font-family: Archivo, sans-serif; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; font-size: 0.78rem; z-index: 5; pointer-events: auto; backdrop-filter: blur(2px); -webkit-backdrop-filter: blur(2px); animation: bb-overlay-in 220ms cubic-bezier(0.16, 1, 0.3, 1) both; }',
      '.bb-loading-overlay__spinner { width: 36px; height: 36px; border: 2px solid rgba(245,245,245,0.18); border-top-color: #CB9228; border-right-color: #CB9228; border-radius: 50%; animation: bb-spin 0.75s cubic-bezier(0.55, 0.15, 0.45, 0.85) infinite; box-shadow: 0 0 18px rgba(203,146,40,0.25); }',
      '.bb-loading-overlay__label { animation: bb-overlay-pulse 1.4s ease-in-out infinite; }',
      '@keyframes bb-spin { to { transform: rotate(360deg); } }',
      '@keyframes bb-overlay-in { from { opacity: 0; } to { opacity: 1; } }',
      '@keyframes bb-overlay-pulse { 0%, 100% { opacity: 0.7; } 50% { opacity: 1; } }'
    ].join('\n');
    document.head.appendChild(style);
  }
  function openDrawerOptimistic() {
    ensureOptimisticStyles();
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return false;
    drawer.classList.remove('is-empty');
    drawer.classList.add('bb-loading');
    const inner = drawer.querySelector('.drawer__inner');
    if (inner && !inner.querySelector('.bb-loading-overlay')) {
      const overlay = document.createElement('div');
      overlay.className = 'bb-loading-overlay';
      overlay.innerHTML = '<div class="bb-loading-overlay__spinner" aria-hidden="true"></div><div class="bb-loading-overlay__label">Preparing your selection\u2026</div>';
      inner.appendChild(overlay);
    }
    if (typeof drawer.open === 'function') drawer.open();
    return true;
  }
  function clearDrawerOptimistic() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.classList.remove('bb-loading');
    const overlay = drawer.querySelector('.bb-loading-overlay');
    if (overlay) overlay.remove();
  }
  function closeDrawerAfterError() {
    clearDrawerOptimistic();
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  }

  // ---- Page-load cart sync --------------------------------------------------
  // Reconcile the discount cache with the real server-side cart state. Prevents
  // a manually-emptied cart from leaving a stale cached discount that would
  // cause us to skip a needed /cart/update.js call on the next click.
  async function syncDiscountCacheFromCart() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();
      let active = '';
      if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length) {
        active = cart.cart_level_discount_applications[0].title || cart.cart_level_discount_applications[0].code || '';
      } else if (Array.isArray(cart.discount_codes)) {
        const hit = cart.discount_codes.find(function (d) { return d && d.applicable; });
        if (hit) active = hit.code || '';
      }
      setCachedDiscountCode(active);
    } catch (e) { /* offline / private mode — leave cache as-is */ }
  }

  // ---- Hover & pointerdown prefetch ----------------------------------------
  // When the user hovers a CTA (or starts pressing it on touch), pre-apply
  // the corresponding discount in the background. The ~570ms /cart/update.js
  // round-trip happens during the natural hover\u2192click delay, so the click
  // itself sees a cache hit and skips the wait entirely.
  function getDiscountForPlan(sectionEl, plan) {
    const ds = sectionEl.dataset;
    if (plan === 'annual' && ds.annualDiscount) return ds.annualDiscount;
    if (plan === 'quarterly' && ds.quarterlyDiscount) return ds.quarterlyDiscount;
    if (plan === 'onetime' && ds.onetimeDiscount) return ds.onetimeDiscount;
    return '';
  }
  function bindPrefetch(cta, sectionEl) {
    let timer = null;
    let inFlight = false;
    const HOVER_DELAY = 60;
    const fire = function () {
      if (inFlight) return;
      const plan = getActivePlan(sectionEl);
      const code = getDiscountForPlan(sectionEl, plan);
      if (!code || getCachedDiscountCode() === code) return;
      inFlight = true;
      applyDiscountCode(code).finally(function () { inFlight = false; });
    };
    cta.addEventListener('mouseenter', function () {
      clearTimeout(timer);
      timer = setTimeout(fire, HOVER_DELAY);
    });
    cta.addEventListener('mouseleave', function () { clearTimeout(timer); });
    // pointerdown beats click by ~50-150ms on touch and mouse — last chance
    // to start the discount apply before the actual click handler runs.
    cta.addEventListener('pointerdown', function () {
      clearTimeout(timer);
      fire();
    });
  }

  function handlePostAddRedirect(mode, discountCode, drawerAlreadyOpening) {
    const normalized = (mode || 'cart').toLowerCase();

    switch (normalized) {
      case 'checkout': {
        let url = '/checkout';
        if (discountCode) {
          url = '/checkout?discount=' + encodeURIComponent(discountCode);
        }
        window.location.assign(url);
        break;
      }
      case 'drawer': {
        // Discount (if any) has already been applied via applyDiscountCode
        // before /cart/add.js, so the rendered sections in the response
        // already reflect the discounted prices. No page navigation needed.
        if (drawerAlreadyOpening) {
          // refreshCartDrawerFromResponse already triggered Dawn's open().
          // Calling it again here would re-trigger the slide-in animation
          // and cause the visible appear/disappear/reappear flicker.
        } else {
          const opened = tryOpenCartDrawer();
          if (!opened) window.location.assign('/cart');
        }
        break;
      }
      case 'cart': {
        let url = '/cart';
        if (discountCode) {
          url = '/discount/' + encodeURIComponent(discountCode) + '?redirect=/cart';
        }
        window.location.assign(url);
        break;
      }
      default:
        window.location.assign('/cart');
        break;
    }
  }

  function setActivePlan(sectionEl, plan) {
    qa(TOGGLE_BTN_SELECTOR, sectionEl).forEach((btn) => {
      const isActive = btn.getAttribute('data-plan-toggle') === plan;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    });

    qa(PLAN_CONTENT_SELECTOR, sectionEl).forEach((card) => {
      const isActive = card.getAttribute('data-plan-content') === plan;
      card.classList.toggle('d-none', !isActive);
    });

    swapPlanImage(sectionEl, plan);
  }

  function getActivePlan(sectionEl) {
    const activeCard = qa(PLAN_CONTENT_SELECTOR, sectionEl).find(
      (el) => !el.classList.contains('d-none')
    );
    if (activeCard) return activeCard.getAttribute('data-plan-content');

    const activeBtn = qa(TOGGLE_BTN_SELECTOR, sectionEl).find((b) =>
      b.classList.contains('is-active')
    );
    return activeBtn ? activeBtn.getAttribute('data-plan-toggle') : 'annual';
  }

function swapPlanImage(sectionEl, plan) {
  const desktopImg = q(PLAN_IMAGE_SELECTOR, sectionEl);
  const mobileImg = q(PLAN_IMAGE_MOBILE_SELECTOR, sectionEl);

  // Desktop image (same behavior as before)
  if (desktopImg) {
    const annualSrc    = desktopImg.getAttribute('data-annual-image') || '';
    const quarterlySrc = desktopImg.getAttribute('data-quarterly-image') || '';
    const onetimeSrc   = desktopImg.getAttribute('data-onetime-image') || '';
    const fallbackSrc  = desktopImg.getAttribute('data-fallback-image') || '';

    let nextDesktopSrc = '';

    if (plan === 'annual') {
      nextDesktopSrc = annualSrc || fallbackSrc;
    } else if (plan === 'quarterly') {
      nextDesktopSrc = quarterlySrc || fallbackSrc;
    } else if (plan === 'onetime') {
      nextDesktopSrc = onetimeSrc || fallbackSrc;
    } else {
      nextDesktopSrc = fallbackSrc || annualSrc || quarterlySrc || onetimeSrc;
    }

    if (nextDesktopSrc && desktopImg.src !== nextDesktopSrc) {
      desktopImg.src = nextDesktopSrc;
    }
  }

  // Mobile image (per-plan, optional)
  if (mobileImg) {
    const annualMobileSrc    = mobileImg.getAttribute('data-annual-mobile-image') || '';
    const quarterlyMobileSrc = mobileImg.getAttribute('data-quarterly-mobile-image') || '';
    const onetimeMobileSrc   = mobileImg.getAttribute('data-onetime-mobile-image') || '';
    const fallbackMobileSrc  = mobileImg.getAttribute('data-mobile-fallback-image') || '';

    let nextMobileSrc = '';

    if (plan === 'annual') {
      nextMobileSrc = annualMobileSrc || fallbackMobileSrc;
    } else if (plan === 'quarterly') {
      nextMobileSrc = quarterlyMobileSrc || fallbackMobileSrc;
    } else if (plan === 'onetime') {
      nextMobileSrc = onetimeMobileSrc || fallbackMobileSrc;
    } else {
      nextMobileSrc = fallbackMobileSrc || annualMobileSrc || quarterlyMobileSrc || onetimeMobileSrc;
    }

    if (nextMobileSrc && mobileImg.src !== nextMobileSrc) {
      mobileImg.src = nextMobileSrc;
    }
  }
}


function buildItemsForPlan(sectionEl, plan) {
  const ds = sectionEl.dataset;
  const ids = {
    annualVariant:    toNum(ds.annualVariant),
    annualPlan:       toNum(ds.annualPlan),
    quarterlyVariant: toNum(ds.quarterlyVariant),
    quarterlyPlan:    toNum(ds.quarterlyPlan),
    annualGlasses:    toNum(ds.annualGlasses),
    quarterlyGlass:   toNum(ds.quarterlyGlass),
    includedSample:   toNum(ds.includedSample),
    oneTimeSample:    toNum(ds.oneTimeSample)
  };

  const items = [];

  if (plan === 'annual') {
    if (!ids.annualVariant || !ids.annualPlan) {
      throw new Error('Annual plan is not configured: missing variant or selling plan ID.');
    }

    // Annual subscription line item – THIS is what Bold/Shopify uses as the subscription
    items.push({
      id: ids.annualVariant,
      quantity: 1,
      selling_plan: ids.annualPlan,
      properties: {
        "_bb_included": "yes",
        "_bb_plan_type": "annual",
        "_bb_subscription": "annual"
      }
    });

    // Free 4-glass set (normal line item, no selling plan)
    if (ids.annualGlasses) {
      items.push({
        id: ids.annualGlasses,
        quantity: 1,
        properties: {
          "_bb_included": "yes",
          "_bb_parent_plan": "annual"
        }
      });
    }

    // 5th Bonus Kit (sample) – included only for annual
    if (ids.includedSample) {
      items.push({
        id: ids.includedSample,
        quantity: 1,
        properties: {
          "_bb_included": "yes",
          "_bb_parent_plan": "annual"
        }
      });
    }
  } else if (plan === 'quarterly') {
    if (!ids.quarterlyVariant || !ids.quarterlyPlan) {
      throw new Error('Quarterly plan is not configured: missing variant or selling plan ID.');
    }

    // Quarterly subscription line item
    items.push({
      id: ids.quarterlyVariant,
      quantity: 1,
      selling_plan: ids.quarterlyPlan,
      properties: {
        "_bb_included": "yes",
        "_bb_plan_type": "quarterly",
        "_bb_subscription": "quarterly"
      }
    });

    // Free quarterly glass (if configured)
    if (ids.quarterlyGlass) {
      items.push({
        id: ids.quarterlyGlass,
        quantity: 1,
        properties: {
          "_bb_included": "yes",
          "_bb_parent_plan": "quarterly"
        }
      });
    }
  } else if (plan === 'onetime') {
    // One-time paid kit only – no selling plan
    if (!ids.oneTimeSample) {
      throw new Error('One-time plan is not configured: missing one-time sample variant ID.');
    }

    // NOTE: do NOT tag this item with _bb_included:"yes" — that flag is reserved
    // for free gifts that depend on a subscription. cart-gift-guard.js removes
    // any _bb_included item when no subscription is present, which would
    // silently delete this paid one-time box the moment the page reloads.
    items.push({
      id: ids.oneTimeSample,
      quantity: 1,
      properties: {
        "_bb_plan_type": "onetime"
      }
    });
  } else {
    throw new Error('Unknown plan: ' + plan);
  }

  return items;
}

  /**
   * Export config for the cart-sample-guard into localStorage.
   * This pulls everything from the section's data-* attributes (which
   * are bound to section.settings in the theme editor).
   */
  function storeGuardConfigFromSection(sectionEl) {
    if (!sectionEl || typeof window === 'undefined' || !window.localStorage) return;

    try {
      const ds = sectionEl.dataset;

      // Free sample + paid fallback
      const freeSampleVariantId = toNum(ds.includedSample);
      const paidSampleVariantId = toNum(ds.sampleFallback);

      // Subscription variants
      const subAnnual = toNum(ds.annualVariant);
      const subQuarterly = toNum(ds.quarterlyVariant);
      const subscriptionVariantIds = [subAnnual, subQuarterly].filter(Boolean);

      // Included glasses (annual + quarterly) – optional but we’ll use them if present
      const freeGlassAnnual = toNum(ds.annualGlasses);
      const freeGlassQuarterly = toNum(ds.quarterlyGlass);
      const freeGlassVariantIds = [freeGlassAnnual, freeGlassQuarterly].filter(Boolean);

      if (!freeSampleVariantId || !paidSampleVariantId || !subscriptionVariantIds.length) {
        // Not fully configured yet – bail out quietly
        return;
      }

      const cfg = {
        freeSampleVariantId,
        paidSampleVariantId,
        subscriptionVariantIds,
        freeGlassVariantIds
      };

      window.localStorage.setItem('bbGuardConfig', JSON.stringify(cfg));
    } catch (e) {
      console.error('[addon-bundler] unable to store cart guard config:', e);
    }
  }

  function bindSection(sectionEl) {
    if (!sectionEl) return;

    // As soon as we see a configured section, export its config for the cart guard.
    storeGuardConfigFromSection(sectionEl);

    // Toggle clicks
    qa(TOGGLE_BTN_SELECTOR, sectionEl).forEach((btn) => {
      btn.addEventListener('click', () => {
        const plan = btn.getAttribute('data-plan-toggle');
        setActivePlan(sectionEl, plan);
      });
    });

    // CTA (Add to cart) clicks — bind hover/pointerdown prefetch first so
    // the discount is already on the cart by the time `click` fires.
    qa(CTA_SELECTOR, sectionEl).forEach((cta) => {
      bindPrefetch(cta, sectionEl);
      cta.addEventListener('click', async (ev) => {
        ev.preventDefault();
        ev.stopPropagation();

        const plan = getActivePlan(sectionEl);
        const ds = sectionEl.dataset;

        let redirectMode = ds.redirectAfterAdd || 'cart';
        if (plan === 'annual' && ds.redirectAnnual) {
          redirectMode = ds.redirectAnnual;
        } else if (plan === 'quarterly' && ds.redirectQuarterly) {
          redirectMode = ds.redirectQuarterly;
        } else if (plan === 'onetime' && ds.redirectOnetime) {
          redirectMode = ds.redirectOnetime;
        }

        let discountCode = '';
        if (plan === 'annual' && ds.annualDiscount) {
          discountCode = ds.annualDiscount;
        } else if (plan === 'quarterly' && ds.quarterlyDiscount) {
          discountCode = ds.quarterlyDiscount;
        } else if (plan === 'onetime' && ds.onetimeDiscount) {
          discountCode = ds.onetimeDiscount;
        }

        cta.disabled = true;
        cta.setAttribute('aria-busy', 'true');

        // Optimistic open: slide the drawer in immediately so the slide-in
        // animation runs in parallel with the network round-trip. The drawer
        // shows a loading overlay until renderContents swaps real content in.
        let optimisticallyOpened = false;
        if (redirectMode === 'drawer') {
          optimisticallyOpened = openDrawerOptimistic();
        }

        try {
          const items = buildItemsForPlan(sectionEl, plan);
          if (!items.length) throw new Error('No items to add for the selected plan.');

          // Apply the discount BEFORE adding the items so the sections HTML
          // returned by /cart/add.js reflects the discounted line prices.
          // Skipped automatically if the same code is already on the cart
          // (see applyDiscountCode + sessionStorage cache).
          if (redirectMode === 'drawer' && discountCode) {
            await applyDiscountCode(discountCode);
          }

          const addResponse = await addItems(items);

          // If we're going to open the drawer (no full-page nav), re-render
          // it from the response so the new line items show up immediately.
          // Dawn's renderContents replaces #CartDrawer innerHTML, which
          // also removes the optimistic loading overlay automatically.
          let drawerRefreshMode = false;
          if (redirectMode === 'drawer') {
            drawerRefreshMode = refreshCartDrawerFromResponse(addResponse);
            clearDrawerOptimistic();
          }

          handlePostAddRedirect(redirectMode, discountCode, drawerRefreshMode === 'native' || optimisticallyOpened);
        } catch (err) {
          console.error('[addon-bundler] CTA error:', err);
          if (optimisticallyOpened) closeDrawerAfterError();
          alert(
            err && err.message
              ? err.message
              : 'Sorry—something went wrong adding your selection. Please refresh and try again.'
          );
        } finally {
          cta.disabled = false;
          cta.removeAttribute('aria-busy');
        }
      });
    });

    // Optional: direct sample-box link (if you use it anywhere)
    const sampleBtn = q(SAMPLE_LINK_SELECTOR, sectionEl);
    if (sampleBtn) {
      sampleBtn.addEventListener('click', async (ev) => {
        ev.preventDefault();
        const ds = sectionEl.dataset;
        const sampleVariant = toNum(ds.oneTimeSample);
        const fallbackUrl = ds.oneTimeSampleUrl || '';
        try {
          if (sampleVariant) {
            const addResponse = await addItems([{ id: sampleVariant, quantity: 1 }]);
            const refreshMode = refreshCartDrawerFromResponse(addResponse);
            // Skip open() when Dawn's renderContents already scheduled it,
            // otherwise we double-fire the slide-in and the drawer flickers.
            if (refreshMode !== 'native') {
              const opened = tryOpenCartDrawer();
              if (!opened) window.location.assign('/cart');
            }
          } else if (fallbackUrl) {
            window.location.assign(fallbackUrl);
          } else {
            alert('Sample box not available right now.');
          }
        } catch (err) {
          console.error('[addon-bundler] Sample add error:', err);
          alert('Could not add the sample box. Please try again.');
        }
      });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    qa(SECTION_SELECTOR).forEach((sectionEl) => {
      bindSection(sectionEl);
      setActivePlan(sectionEl, 'annual');
    });

    // Sync the discount cache against the real cart so we never skip a
    // needed /cart/update.js call because of stale sessionStorage.
    syncDiscountCacheFromCart();

    // Auto-open drawer after /discount/CODE redirect.
    // addon-bundler sets ?bb_open_drawer=1 as a signal on the return URL.
    // (Legacy: kept for any in-flight links / bookmarks that still use this
    // signal. The drawer flow now applies discounts in-place via AJAX, so
    // new clicks will not produce this URL parameter.)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('bb_open_drawer') === '1') {
      urlParams.delete('bb_open_drawer');
      const cleanUrl = window.location.pathname +
        (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', cleanUrl);
      setTimeout(function () {
        const opened = tryOpenCartDrawer();
        if (!opened) window.location.assign('/cart');
      }, 400);
    }
  });
})();
